import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getChatActivity,
  subscribeChatActivity,
} from '@/components/chat/chatActivityStore';
import {
  fetchLastMessagesForChats,
  type LastMessageInfo,
} from '@/components/chat/fetchLastMessagesForChats';
import { formatChatMessagePreview } from '@/components/chat/formatChatMessagePreview';
import { chatKey, chatKeyToTarget } from '@/components/chat/chatTarget';
import type { ChatMessage, ChatTarget } from '@/components/chat/types';
import { useAuth } from '@/contexts/AuthContext';
import { useDepartmentChats } from '@/hooks/useDepartmentChats';
import { useDirectChats } from '@/hooks/useDirectChats';
import { useChatMute } from '@/hooks/useChatMute';
import { useChatUnread } from '@/hooks/useChatUnread';
import { useOpenDirectChat } from '@/hooks/useOpenDirectChat';
import { useEffectiveOrgId } from '@/hooks/useEffectiveOrgId';
import { cacheRows, cachedRows } from '@/utils/chatListCache';
import { isEventLive } from '@/utils/isEventLive';
import { supabase } from '@/lib/supabase';
import { pickNewestPreview } from './pickNewestPreview';
import { useChatListResync } from './useChatListResync';

/** One event the viewer can switch to. The page shows exactly one at a time. */
export type CommunicationEvent = {
  id: string;
  name: string;
  image: string | null;
};

/**
 * One row in a section. The three sections render differently, but they all need the
 * same underlying facts, so they share a shape rather than each inventing one.
 */
export type ChatListItem = {
  key: string;
  target: ChatTarget;
  kind: 'event' | 'direct' | 'department';
  title: string;
  /** Event name under a person's name, so two threads from the same org are tellable apart. */
  subtitle: string | null;
  image: string | null;
  createdAt: string;
  lastMessageAt: string | null;
  preview: string;
  /** Notifications silenced for this thread. The count is unaffected -- muting a
   *  conversation quiets it, it does not hide it. */
  muted: boolean;
  /**
   * Messages waiting in this thread. Drives every unread signal on the row: the badge,
   * the bold title, the darker preview.
   *
   * This used to be a boolean derived from a one-off read-receipt query, which went
   * stale the moment the viewer read a thread -- the row stayed bold until something
   * else reloaded the lists. The count comes from ChatUnreadContext, which a read
   * receipt updates over realtime, so leaving a chat clears it immediately.
   */
  unreadCount: number;
  /** The event this thread belongs to, for the "..." details modal. */
  eventId: string;
  eventName: string | null;
  eventImage: string | null;
};

type EventChatBaseRow = {
  id: string;
  event_id: string;
  name: string | null;
  image: string | null;
  created_at: string;
  last_message_at: string | null;
};

const EVENT_PARAM = 'event';
const CHAT_PARAM = 'chat';

/**
 * Everything the Communication page knows.
 *
 * The page is scoped to one event: the general chat, department chats and private threads
 * on screen all share an event_id, chosen with the header selector and carried in the URL
 * as ?event=. Sections are returned separately rather than merged into one list, because
 * the redesign gives each its own anatomy.
 */
export function useCommunicationPage() {
  const { user: authUser, loading: authLoading, authStatus } = useAuth();
  const userId = authUser?.id ?? null;
  const authSettling = authLoading || authStatus === 'loading';

  const { isOrgView, effectiveOrgId } = useEffectiveOrgId();
  const { chats: directChats, loading: directLoading, refetch: refetchDirectChats } =
    useDirectChats(userId, authSettling);
  const { openChat } = useOpenDirectChat();
  const { isMuted, mutedUntil, mute, unmute } = useChatMute(userId);
  const { countByKey, refetch: refetchUnread } = useChatUnread();

  /**
   * Messages seen live since the lists were fetched.
   *
   * Fed by the one messages_new subscription in ChatUnreadProvider. Read from a module
   * store rather than a context so a message arriving anywhere in the app does not
   * re-render every screen that shows the nav badge.
   */
  const liveActivity = useSyncExternalStore(
    subscribeChatActivity,
    getChatActivity,
    getChatActivity
  );

  const [searchParams, setSearchParams] = useSearchParams();

  // Same identity the reload logic below keys on: a viewer, as whichever org they act as.
  const eventChatsKey = userId ? `events:${userId}|${effectiveOrgId ?? ''}` : null;
  // Seeded rather than filled by an effect: an effect runs after the first paint, which is
  // the blank list we are trying not to show. See utils/chatListCache.
  const [eventChats, setEventChats] = useState<EventChatBaseRow[]>(
    () => cachedRows<EventChatBaseRow>(eventChatsKey) ?? []
  );
  const [loading, setLoading] = useState(() => cachedRows(eventChatsKey) == null);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Map<string, LastMessageInfo>>(new Map());
  /** Local last-message overrides so sending reorders the list without a refetch. */
  const [sentOverrides, setSentOverrides] = useState<
    Record<string, { lastMessageAt: string; preview: string }>
  >({});
  /**
   * Seeded from ?chat= so a thread named in the URL is selected on the first render
   * rather than after the deep-link effect below has run. With the lists warm from the
   * cache that means the conversation is simply there when the page mounts -- coming back
   * from a profile no longer shows the list first and the thread a moment later.
   *
   * A legacy bare-id link does not match any item key, so it stays unselected here and
   * the effect resolves it properly once the lists are in.
   */
  const [selectedKey, setSelectedKey] = useState<string | null>(
    () => searchParams.get(CHAT_PARAM)
  );
  /**
   * A thread we have just opened and want selected as soon as it appears.
   *
   * Selecting it directly is a race: the row has to have arrived from the refetch, the
   * URL has to already name its event, and the "drop a selection that is no longer on
   * screen" effect has to not run in between. Whichever render loses that race clears the
   * key, and because a thread with no messages is only listed while it is selected, the
   * row then vanishes and can never be picked again. Holding the intent instead lets the
   * effect below apply it on whatever render the thread is genuinely there.
   */
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  /** Events this viewer runs. Empty for a volunteer, and for an admin who has not
   *  switched into org context -- the UI fails closed, which is the right direction. */
  const [ownedEventIds, setOwnedEventIds] = useState<Set<string>>(() => new Set());

  // ---- event selection ----
  const events = useMemo<CommunicationEvent[]>(() => {
    const byId = new Map<string, CommunicationEvent>(
      eventChats.map((c) => [
        c.event_id,
        { id: c.event_id, name: c.name ?? 'Unnamed event', image: c.image },
      ])
    );

    // A private thread outlives the volunteer's active membership: direct_chats admits
    // volunteer_id = auth.uid() with no is_active test, while eventChats only lists events
    // they are currently active on. Without this, deactivating someone would strand their
    // threads on an event the selector cannot reach. Department chats need no equivalent
    // -- their predicate requires an active volunteers row, so their event is always
    // already listed.
    for (const c of directChats) {
      if (byId.has(c.eventId)) continue;
      byId.set(c.eventId, { id: c.eventId, name: c.eventName ?? 'Unnamed event', image: null });
    }

    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [eventChats, directChats]);

  /**
   * Newest message per event -- drives the default pick.
   *
   * Department chats are deliberately not consulted. They are now fetched FOR the selected
   * event, so letting them decide which event is selected would be circular.
   *
   * Live messages are deliberately not consulted either. This feeds nothing but the
   * DEFAULT event pick -- the selector itself sorts by name -- so making it live would
   * mean a message on another event silently switching the whole page out from under
   * someone mid-read. Rows on other events are still kept current; switching to one shows
   * them already correct.
   */
  const lastActivityByEvent = useMemo(() => {
    const byEvent = new Map<string, number>();
    const note = (eventId: string, iso: string | null) => {
      if (!iso) return;
      const t = new Date(iso).getTime();
      if (Number.isNaN(t)) return;
      byEvent.set(eventId, Math.max(byEvent.get(eventId) ?? 0, t));
    };
    for (const c of eventChats) note(c.event_id, c.last_message_at);
    for (const c of directChats) note(c.eventId, c.lastMessageAt);
    return byEvent;
  }, [eventChats, directChats]);

  const requestedEventId = searchParams.get(EVENT_PARAM);
  const selectedEventId = useMemo(() => {
    if (events.length === 0) return null;
    if (requestedEventId && events.some((e) => e.id === requestedEventId)) {
      return requestedEventId;
    }
    // Default: wherever the conversation is. Ties and silence fall back to the event whose
    // chat row is newest, which is the most recently created event.
    const byActivity = [...events].sort((a, b) => {
      const diff = (lastActivityByEvent.get(b.id) ?? 0) - (lastActivityByEvent.get(a.id) ?? 0);
      if (diff !== 0) return diff;
      const aRow = eventChats.find((c) => c.event_id === a.id);
      const bRow = eventChats.find((c) => c.event_id === b.id);
      return (
        new Date(bRow?.created_at ?? 0).getTime() - new Date(aRow?.created_at ?? 0).getTime()
      );
    });
    return byActivity[0]?.id ?? null;
  }, [events, requestedEventId, lastActivityByEvent, eventChats]);

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId) ?? null,
    [events, selectedEventId]
  );

  const selectEvent = useCallback(
    (eventId: string) => {
      // Switching events by hand abandons whatever was open; handleStartDirectChat sets
      // its pending key AFTER calling this, so it is unaffected.
      setSelectedKey(null);
      setPendingKey(null);
      const next = new URLSearchParams(searchParams);
      next.set(EVENT_PARAM, eventId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const {
    chats: departmentChats,
    loading: departmentLoading,
    refetch: refetchDepartmentChats,
  } = useDepartmentChats(userId, authSettling, selectedEventId);

  const listLoading = loading || directLoading || departmentLoading;

  // ---- event chats ----
  // Bumped when the viewer's event membership changes under us -- leaving an event has to
  // drop its chat from the list, or the row stays clickable and every read fails RLS.
  const [eventChatsReloadKey, setEventChatsReloadKey] = useState(0);
  const refetchEventChats = useCallback(() => setEventChatsReloadKey((n) => n + 1), []);
  /**
   * Whose list is currently on screen.
   *
   * A reload only wipes what is rendered when the ANSWER could belong to someone else.
   * Without this, every background resync blanked the list and showed a skeleton for the
   * length of three round trips.
   */
  const eventChatsIdentityRef = useRef<string | null>(null);

  // Every event gets a chat_new row from the after_event_insert_create_chat_new trigger,
  // so this doubles as the event list the header selector offers.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setError(null);
      if (authSettling) {
        setLoading(true);
        return;
      }
      if (!userId) {
        setEventChats([]);
        setLoading(false);
        return;
      }

      // Drop the previous VIEWER's list immediately so we never flash their rows if an
      // older request resolves late. A reload for the same viewer keeps what is on screen
      // until the new answer arrives -- see eventChatsIdentityRef.
      const identity = `${userId}|${effectiveOrgId ?? ''}`;
      // The same key eventChatsKey builds, from this run's own identity rather than the
      // render's, so the effect does not depend on it.
      const listCacheKey = `events:${identity}`;
      if (eventChatsIdentityRef.current !== identity) {
        eventChatsIdentityRef.current = identity;
        // Rows for this identity from an earlier mount stand in until the fetch answers,
        // so returning to the page (from a profile, say) does not blank it.
        const cached = cachedRows<EventChatBaseRow>(listCacheKey);
        setEventChats(cached ?? []);
        setLoading(cached == null);
      }

      try {
        // A volunteer acting as an org admin does not own the org's events: EventModal is
        // handed activeAdminOrgId (OrganizationsPage:351) and useEventFormBase:344 writes
        // that as created_by, so created_by is the ORG's profile id, never theirs.
        // Filtering on userId alone left them with no events -- and because every section
        // is scoped to the selected event, with no chats either.
        const ownerIds = [...new Set([userId, effectiveOrgId].filter(Boolean) as string[])];

        const [createdRes, volunteersRes] = await Promise.all([
          supabase.from('events').select('id').in('created_by', ownerIds),
          supabase
            .from('volunteers')
            .select('event_id')
            .eq('volunteer_id', userId)
            .eq('is_active', true),
        ]);

        if (cancelled) return;

        const creatorIds = (createdRes.data ?? []).map((r) => r.id);
        // Exactly "the events I own as the org I am currently acting as" -- ownerIds is
        // [userId, effectiveOrgId]. That is the client-side twin of is_event_org_admin(),
        // and it is what gates pinning. It was already being computed here and discarded.
        setOwnedEventIds(new Set<string>(creatorIds));
        const volunteerIds = (volunteersRes.data ?? []).map(
          (r) => (r as { event_id: string }).event_id
        );
        const myEventIds = [...new Set([...creatorIds, ...volunteerIds])];

        if (myEventIds.length === 0) {
          const empty: EventChatBaseRow[] = [];
          cacheRows(listCacheKey, empty);
          setEventChats(empty);
          return;
        }

        const { data: chatData, error: chatError } = await supabase
          .from('chat_new')
          .select('id, event_id, name, image, created_at, last_message_at, events(end_date, is_active)')
          .in('event_id', myEventIds);

        if (cancelled) return;

        if (chatError) {
          console.error('[CommunicationPage] Failed to load event chats', chatError);
          setError('Failed to load event chats.');
          setEventChats([]);
          return;
        }

        const now = Date.now();
        const rows: EventChatBaseRow[] = (chatData ?? [])
          .map((row: Record<string, unknown>) => {
            const events = row.events as
              | { end_date?: string | null; is_active?: boolean | null }
              | { end_date?: string | null; is_active?: boolean | null }[]
              | null;
            const ev = Array.isArray(events) ? events[0] : events;
            if (!isEventLive(ev, now)) return null;
            return {
              id: row.id as string,
              event_id: row.event_id as string,
              name: (row.name as string | null) ?? null,
              image: (row.image as string | null) ?? null,
              created_at: row.created_at as string,
              last_message_at: (row.last_message_at as string | null) ?? null,
            };
          })
          .filter((r): r is EventChatBaseRow => r != null);

        // Only a successful read is remembered, so an error leaves the last good rows for
        // the next mount rather than caching a blank list.
        cacheRows(listCacheKey, rows);
        setEventChats(rows);
      } catch (err) {
        console.error('[CommunicationPage] Failed to load chats', err);
        if (!cancelled) {
          setError('Failed to load event chats.');
          setEventChats([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [authSettling, userId, effectiveOrgId, eventChatsReloadKey]);

  /**
   * Bring the lists back in step after a spell in the background.
   *
   * Realtime keeps listed threads current while the page is on screen, but the socket is
   * the first thing to go when the app is backgrounded -- and a thread that did not exist
   * at fetch time has no row to update, so only a refetch can surface it. Quiet, so
   * returning to the tab does not replace a good list with a skeleton.
   */
  const resyncLists = useCallback(() => {
    refetchEventChats();
    void refetchDirectChats({ quiet: true });
    void refetchDepartmentChats({ quiet: true });
  }, [refetchEventChats, refetchDirectChats, refetchDepartmentChats]);

  useChatListResync(resyncLists, !authSettling && !!userId);

  // ---- last-message previews for all three kinds in one pass ----
  const previewSourceKey = useMemo(
    () =>
      [
        ...eventChats.map((c) => `e:${c.event_id}:${c.last_message_at ?? ''}`),
        ...departmentChats.map((c) => `p:${c.id}:${c.lastMessageAt ?? ''}`),
        ...directChats.map((c) => `d:${c.id}:${c.lastMessageAt ?? ''}`),
      ].join('|'),
    [eventChats, departmentChats, directChats]
  );

  useEffect(() => {
    let cancelled = false;
    if (!previewSourceKey) {
      setPreviews(new Map());
      return;
    }
    const targets = [
      ...eventChats.map((c) => ({
        target: { kind: 'event' as const, eventId: c.event_id },
        last_message_at: c.last_message_at,
      })),
      ...departmentChats.map((c) => ({
        target: { kind: 'department' as const, departmentChatId: c.id },
        last_message_at: c.lastMessageAt,
      })),
      ...directChats.map((c) => ({
        target: { kind: 'direct' as const, directChatId: c.id },
        last_message_at: c.lastMessageAt,
      })),
    ];
    // Read receipts are no longer fetched here: unread is now a live count from
    // ChatUnreadContext, which this query only ever approximated one message at a time.
    void fetchLastMessagesForChats(targets).then((map) => {
      if (cancelled) return;
      setPreviews(map);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previewSourceKey encodes all lists
  }, [previewSourceKey, userId]);

  // ---- items, by kind ----
  const decorate = useCallback(
    (item: ChatListItem): ChatListItem => {
      /**
       * Newest of the three things that can describe this row's last message.
       *
       * The live entry is offered first so it wins a tie, and a tie is the common case
       * right after a list refetch: the row's last_message_at is fresh but the matching
       * preview text has not landed yet, so the fetched candidate would read "No messages
       * yet" against a brand-new timestamp.
       *
       * Comparing rather than layering also stops the local echo being sticky -- it used
       * to outrank every later refetch for the life of the page.
       */
      const newest = pickNewestPreview(
        liveActivity.get(item.key),
        sentOverrides[item.key],
        item
      );
      return {
        ...item,
        lastMessageAt: newest?.lastMessageAt ?? item.lastMessageAt,
        preview: newest?.preview ?? item.preview,
        muted: isMuted(item.key),
        // The thread on screen is being read right now.
        unreadCount: item.key === selectedKey ? 0 : (countByKey.get(item.key) ?? 0),
      };
    },
    [liveActivity, sentOverrides, selectedKey, isMuted, countByKey]
  );

  const eventItems = useMemo<ChatListItem[]>(
    () =>
      eventChats.map((c) => {
        const target: ChatTarget = { kind: 'event', eventId: c.event_id };
        const key = chatKey(target);
        return decorate({
          key,
          target,
          kind: 'event',
          title: 'General chat',
          subtitle: null,
          image: c.image,
          createdAt: c.created_at,
          lastMessageAt: c.last_message_at,
          preview: previews.get(key)?.preview ?? 'No messages yet',
          muted: false,
          unreadCount: 0,
          eventId: c.event_id,
          eventName: c.name,
          eventImage: c.image,
        });
      }),
    [eventChats, previews, decorate]
  );

  const departmentItems = useMemo<ChatListItem[]>(
    () =>
      departmentChats.map((c) => {
        const target: ChatTarget = { kind: 'department', departmentChatId: c.id };
        const key = chatKey(target);
        return decorate({
          key,
          target,
          kind: 'department',
          title: c.department,
          subtitle: c.eventName,
          image: c.eventImage,
          createdAt: c.createdAt,
          lastMessageAt: c.lastMessageAt,
          preview: previews.get(key)?.preview ?? 'No messages yet',
          muted: false,
          unreadCount: 0,
          eventId: c.eventId,
          eventName: c.eventName,
          eventImage: c.eventImage,
        });
      }),
    [departmentChats, previews, decorate]
  );

  const directItems = useMemo<ChatListItem[]>(
    () =>
      directChats
        .map((c) => {
          const target: ChatTarget = { kind: 'direct', directChatId: c.id };
          const key = chatKey(target);
          return decorate({
            key,
            target,
            kind: 'direct',
            title: c.counterpartName,
            subtitle: c.eventName,
            image: c.counterpartImage,
            createdAt: c.createdAt,
            lastMessageAt: c.lastMessageAt,
            preview: previews.get(key)?.preview ?? 'No messages yet',
            muted: false,
            unreadCount: 0,
            eventId: c.eventId,
            eventName: c.eventName,
            eventImage: null,
          });
        })
        // A private thread with no messages yet is not a conversation, so it stays out of
        // the list -- picking someone from the + sheet and then backing out without typing
        // should leave no trace. The one currently open is kept so it does not vanish from
        // under the user while they are composing. General and department chats are never
        // filtered: they exist whether or not anyone has posted.
        .filter(
          (item) =>
            item.lastMessageAt != null ||
            item.key === selectedKey ||
            item.key === pendingKey
        )
        .sort((a, b) => {
          const aTime = a.lastMessageAt ?? a.createdAt;
          const bTime = b.lastMessageAt ?? b.createdAt;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        }),
    [directChats, previews, decorate, selectedKey, pendingKey]
  );

  // ---- sections, scoped to the selected event and the search box ----
  const matchesQuery = useCallback(
    (item: ChatListItem) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return (
        item.title.toLowerCase().includes(q) || (item.subtitle ?? '').toLowerCase().includes(q)
      );
    },
    [searchQuery]
  );

  const generalChat = useMemo(
    () =>
      eventItems.find((i) => i.eventId === selectedEventId && matchesQuery(i)) ?? null,
    [eventItems, selectedEventId, matchesQuery]
  );

  const departmentSection = useMemo(
    () => departmentItems.filter((i) => i.eventId === selectedEventId && matchesQuery(i)),
    [departmentItems, selectedEventId, matchesQuery]
  );

  const directSection = useMemo(
    () => directItems.filter((i) => i.eventId === selectedEventId && matchesQuery(i)),
    [directItems, selectedEventId, matchesQuery]
  );

  /**
   * Everything on the selected event, ignoring the search box.
   *
   * The selection effects below work off this rather than the rendered sections. Search
   * is a filter on what is DISPLAYED, not on what exists: testing selection against the
   * filtered list meant typing a query that did not match the open thread closed the
   * conversation mid-read.
   */
  const eventScopedItems = useMemo(
    () =>
      [...eventItems, ...departmentItems, ...directItems].filter(
        (i) => i.eventId === selectedEventId
      ),
    [eventItems, departmentItems, directItems, selectedEventId]
  );

  const selected = useMemo(
    () =>
      [...eventItems, ...departmentItems, ...directItems].find((i) => i.key === selectedKey) ??
      null,
    [eventItems, departmentItems, directItems, selectedKey]
  );

  const directChatsById = useMemo(
    () => new Map(directChats.map((c) => [c.id, c])),
    [directChats]
  );

  /** Locate a deep-linked thread among the lists on hand, and say which event it is on. */
  const resolveDeepLinkTarget = useCallback(
    (target: ChatTarget): { target: ChatTarget; eventId: string } | null => {
      switch (target.kind) {
        case 'event': {
          const row = eventChats.find((c) => c.event_id === target.eventId);
          return row ? { target, eventId: row.event_id } : null;
        }
        case 'direct': {
          const row = directChats.find((c) => c.id === target.directChatId);
          return row ? { target, eventId: row.eventId } : null;
        }
        case 'department': {
          const row = departmentChats.find((c) => c.id === target.departmentChatId);
          return row ? { target, eventId: row.eventId } : null;
        }
      }
    },
    [eventChats, directChats, departmentChats]
  );

  // ---- deep link: ?chat=<chat key>, or a bare direct/department chat id ----
  //
  // Chat notifications link with the chat_key their row carries ('event:<id>',
  // 'department:<id>', 'direct:<id>') -- the only form that can name a general chat.
  // A bare id is still accepted: that is what direct_message rows written before
  // 20260909120100_chat_message_notifications.sql linked with, and what any bookmark
  // from before then holds.
  //
  // Department chats are fetched FOR the selected event, so a link to one on another
  // event only resolves once ?event= has switched the page over. The notification URL
  // carries both parameters for exactly that reason.
  const requestedChat = searchParams.get(CHAT_PARAM);
  useEffect(() => {
    if (!requestedChat) return;
    if (listLoading) return;

    const parsed = chatKeyToTarget(requestedChat);
    const found = parsed
      ? resolveDeepLinkTarget(parsed)
      : (resolveDeepLinkTarget({ kind: 'direct', directChatId: requestedChat }) ??
        resolveDeepLinkTarget({ kind: 'department', departmentChatId: requestedChat }));
    if (!found) return;

    const next = new URLSearchParams(searchParams);
    next.delete(CHAT_PARAM);
    // The thread's event has to be the selected one, or the section it lives in is not
    // rendered and the selection would be dropped by the effect below.
    next.set(EVENT_PARAM, found.eventId);
    setSearchParams(next, { replace: true });
    setSelectedKey(chatKey(found.target));
  }, [requestedChat, listLoading, resolveDeepLinkTarget, searchParams, setSearchParams]);

  // Opening a thread writes its read receipts, which normally reach ChatUnreadContext
  // over realtime. Asking for a refetch on every selection change makes leaving a chat
  // clear its count even when that socket is down -- otherwise the row goes bold again
  // the moment it is deselected. The refetch is debounced, so this is close to free.
  useEffect(() => {
    refetchUnread();
  }, [selectedKey, refetchUnread]);

  // A different viewer starts clean. Only on a change, not on mount: with the lists warm
  // from the cache, the deep-link effect above selects its thread and drops ?chat= in this
  // same pass, so a reset here undid it -- the thread rendered once, marking it read, and
  // the page then showed the list with nothing left in the URL to reopen it.
  const selectionUserIdRef = useRef(userId);
  useEffect(() => {
    if (selectionUserIdRef.current === userId) return;
    selectionUserIdRef.current = userId;
    setSelectedKey(null);
    setPendingKey(null);
    setSearchQuery('');
    setSentOverrides({});
  }, [userId]);

  // A selection that is no longer on screen -- the event changed, the thread was filtered
  // out, the roster changed -- is dropped rather than left pointing at nothing.
  useEffect(() => {
    if (!pendingKey || listLoading) return;
    if (!eventScopedItems.some((c) => c.key === pendingKey)) return;
    setSelectedKey(pendingKey);
    setPendingKey(null);
  }, [pendingKey, eventScopedItems, listLoading]);

  useEffect(() => {
    if (!selectedKey || listLoading || pendingKey) return;
    if (!eventScopedItems.some((c) => c.key === selectedKey)) {
      setSelectedKey(null);
    }
  }, [eventScopedItems, selectedKey, listLoading, pendingKey]);

  // ---- actions ----
  const handleStartDirectChat = useCallback(
    async (eventId: string, volunteerId: string) => {
      if (!effectiveOrgId) return;
      const result = await openChat(eventId, volunteerId, effectiveOrgId);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      await refetchDirectChats();
      // The picker spans every live event the org runs, so the volunteer chosen is often
      // on a different event than the one on screen. Without switching to it the new
      // thread falls outside directSection, the stale-selection effect drops it, and the
      // modal closes onto nothing. selectEvent clears the selection itself, so it has to
      // run first.
      selectEvent(eventId);
      setPendingKey(chatKey({ kind: 'direct', directChatId: result.chatId }));
    },
    [effectiveOrgId, openChat, refetchDirectChats, selectEvent]
  );

  /**
   * Keep the list in step with a message this viewer just sent.
   *
   * Notifying the other side is NOT done here any more: notify_chat_message(), an
   * AFTER INSERT trigger on messages_new, fans every chat message out to the whole
   * thread. The client could only ever notify a direct chat's single counterpart -- RLS
   * gives it no way to write a notifications row for another volunteer -- so group
   * chats had no notifications at all. See
   * supabase/migrations/20260909120100_chat_message_notifications.sql.
   */
  const handleMessageSent = useCallback((item: ChatListItem, message: ChatMessage) => {
    setSentOverrides((prev) => ({
      ...prev,
      [item.key]: {
        lastMessageAt: message.created_at,
        preview: formatChatMessagePreview(message),
      },
    }));
  }, []);

  return {
    // viewer
    isOrgView,
    effectiveOrgId,
    ownedEventIds,
    userId,
    // events
    events,
    selectedEvent,
    selectedEventId,
    selectEvent,
    // sections
    generalChat,
    departmentSection,
    directSection,
    // selection
    selected,
    selectedKey,
    setSelectedKey,
    // state
    listLoading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    directChatsById,
    // mute
    mutedUntil,
    mute,
    unmute,
    // actions
    handleStartDirectChat,
    handleMessageSent,
    refetchEventChats,
    refetchDirectChats,
  };
}
